import { useEffect, useState, useCallback, useRef } from "react";
import { Palette, RotateCcw, Save, Upload, ImageIcon, Type, MousePointerClick, LogIn, Menu as MenuIcon, ShieldAlert } from "lucide-react";
import { api, useAuth, formatApiError } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { RoleBadge } from "../components/RoleBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const POLICES = ["Outfit", "Inter", "Poppins", "Montserrat", "JetBrains Mono", "Georgia", "Arial"];

const COLOR_FIELDS = [
  { key: "fond", label: "Fond des pages" },
  { key: "cartes", label: "Cartes & panneaux" },
  { key: "texte", label: "Texte principal" },
  { key: "accent", label: "Accent (liens, surbrillances)" },
  { key: "boutons", label: "Boutons principaux" },
];

const MENU_FIELDS = [
  { key: "dashboard", def: "Tableau de bord" },
  { key: "salaries", def: "Salariés" },
  { key: "certificats", def: "Certificats SST" },
  { key: "ssiap", def: "SSIAP" },
  { key: "carte_pro", def: "Cartes Pro" },
  { key: "incendie", def: "Formations Incendie" },
  { key: "sessions", def: "Sessions MAC" },
  { key: "rappels", def: "Rappels" },
  { key: "actualites", def: "Actualités" },
  { key: "journal", def: "Journal" },
  { key: "utilisateurs", def: "Utilisateurs" },
  { key: "personnalisation", def: "Personnalisation" },
  { key: "mises_a_jour", def: "Mises à jour" },
];

const EMPTY = { site: {}, couleurs: { sombre: {}, clair: {} }, typo: {}, boutons: {}, login: {}, menu: {} };

function Section({ icon: Icon, titre, desc, children, testid }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid={testid}>
      <div>
        <h2 className="text-base font-bold flex items-center gap-2"><Icon className="h-4 w-4 text-accent" /> {titre}</h2>
        {desc && <p className="text-xs text-muted-foreground mt-1">{desc}</p>}
      </div>
      {children}
    </section>
  );
}

function ColorPicker({ value, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-3">
      <input type="color" value={value || "#888888"} onChange={(e) => onChange(e.target.value)}
        className="h-9 w-12 cursor-pointer rounded-lg border border-border bg-transparent p-0.5" data-testid={testid} />
      <span className="text-sm flex-1">{label}</span>
      {value
        ? <button type="button" onClick={() => onChange("")} className="text-[11px] text-muted-foreground underline" data-testid={`${testid}-reset`}>défaut</button>
        : <span className="text-[11px] text-muted-foreground font-mono">défaut</span>}
    </label>
  );
}

function ImageUpload({ kind, label, desc }) {
  const { imageUrl, refresh } = useBranding();
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const current = imageUrl(kind);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/branding/image/${kind}`, fd);
      toast.success("Image mise à jour");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e, "Envoi impossible"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid={`upload-${kind}`}>
      <div className="h-16 w-16 rounded-xl border border-border bg-secondary/40 flex items-center justify-center overflow-hidden shrink-0">
        {current ? <img src={current} alt={label} className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{desc} — PNG/JPG/WEBP/GIF, 2 Mo max.</p>
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`upload-${kind}-input`} />
      <Button variant="outline" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`upload-${kind}-button`}>
        <Upload className="h-4 w-4 mr-2" /> {uploading ? "Envoi..." : current ? "Remplacer" : "Choisir"}
      </Button>
    </div>
  );
}

export default function Personnalisation() {
  const { user } = useAuth();
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      site: { ...(branding.site || {}) },
      couleurs: { sombre: { ...(branding.couleurs?.sombre || {}) }, clair: { ...(branding.couleurs?.clair || {}) } },
      typo: { ...(branding.typo || {}) },
      boutons: { ...(branding.boutons || {}) },
      login: { ...(branding.login || {}) },
      menu: { ...(branding.menu || {}) },
    });
  }, [branding]);

  const set = useCallback((path, value) => {
    setForm((f) => {
      const copy = { ...f };
      const keys = path.split(".");
      let node = copy;
      for (let i = 0; i < keys.length - 1; i++) node = node[keys[i]] = { ...node[keys[i]] };
      if (value === "" ) delete node[keys[keys.length - 1]];
      else node[keys[keys.length - 1]] = value;
      return copy;
    });
  }, []);

  if (user?.role !== "admin") {
    return (
      <div className="rounded-xl border border-red-800 bg-red-950/30 p-10 text-center" data-testid="perso-forbidden">
        <ShieldAlert className="h-10 w-10 text-amber-400 mx-auto mb-3" />
        <p className="font-semibold text-red-400">Accès réservé à l'administrateur</p>
      </div>
    );
  }

  const save = async (cfg, message) => {
    setSaving(true);
    try {
      await api.put("/branding", { branding: cfg });
      toast.success(message);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e, "Enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="personnalisation-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Apparence</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Personnalisation du site</h1>
          <p className="text-sm text-muted-foreground mt-1">Modifiez l'identité, les couleurs, les polices, les boutons, la page de connexion et les libellés du menu. Visible par tous les utilisateurs.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => save({}, "Personnalisation réinitialisée")} disabled={saving} data-testid="perso-reset-button">
            <RotateCcw className="h-4 w-4 mr-2" /> Tout réinitialiser
          </Button>
          <Button onClick={() => save(form, "Personnalisation enregistrée")} disabled={saving} data-testid="perso-save-button">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </div>

      <Section icon={ImageIcon} titre="Identité du site" desc="Nom affiché dans le menu, sur la page de connexion et dans l'onglet du navigateur." testid="perso-section-identite">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Titre (ligne 1)</Label>
            <Input value={form.site.titre_l1 || ""} onChange={(e) => set("site.titre_l1", e.target.value)} placeholder="Suivi des Certifications" className="bg-background" data-testid="perso-titre-l1" />
          </div>
          <div className="space-y-2">
            <Label>Titre (ligne 2)</Label>
            <Input value={form.site.titre_l2 || ""} onChange={(e) => set("site.titre_l2", e.target.value)} placeholder="/ Formations" className="bg-background" data-testid="perso-titre-l2" />
          </div>
          <div className="space-y-2">
            <Label>Signature</Label>
            <Input value={form.site.signature || ""} onChange={(e) => set("site.signature", e.target.value)} placeholder="by Gerlier Sean" className="bg-background" data-testid="perso-signature" />
          </div>
        </div>
        <ImageUpload kind="logo" label="Logo du site" desc="Menu, page de connexion, mobile" />
      </Section>

      {["sombre", "clair"].map((theme) => (
        <Section key={theme} icon={Palette} titre={`Couleurs — thème ${theme}`} desc="Laissez sur « défaut » pour conserver la couleur d'origine." testid={`perso-section-couleurs-${theme}`}>
          <div className="grid gap-3 sm:grid-cols-2">
            {COLOR_FIELDS.map((c) => (
              <ColorPicker key={c.key} label={c.label} value={form.couleurs[theme][c.key] || ""}
                onChange={(v) => set(`couleurs.${theme}.${c.key}`, v)} testid={`perso-color-${theme}-${c.key}`} />
            ))}
          </div>
        </Section>
      ))}

      <Section icon={Type} titre="Typographie" desc="Polices chargées depuis Google Fonts (sauf Georgia / Arial, déjà sur les appareils)." testid="perso-section-typo">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Police des titres</Label>
            <Select value={form.typo.police_titres || "defaut"} onValueChange={(v) => set("typo.police_titres", v === "defaut" ? "" : v)}>
              <SelectTrigger className="bg-background" data-testid="perso-police-titres"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="defaut">Défaut (Outfit)</SelectItem>
                {POLICES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Police du texte</Label>
            <Select value={form.typo.police_texte || "defaut"} onValueChange={(v) => set("typo.police_texte", v === "defaut" ? "" : v)}>
              <SelectTrigger className="bg-background" data-testid="perso-police-texte"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="defaut">Défaut (Inter)</SelectItem>
                {POLICES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Taille de base du texte : {form.typo.taille_base || 16}px (défaut 16)</Label>
            <Slider value={[form.typo.taille_base || 16]} min={12} max={20} step={1}
              onValueChange={([v]) => set("typo.taille_base", v === 16 ? "" : v)} data-testid="perso-taille-base" />
          </div>
        </div>
      </Section>

      <Section icon={MousePointerClick} titre="Boutons" testid="perso-section-boutons">
        <div className="grid gap-4 sm:grid-cols-2 items-center">
          <div className="space-y-2">
            <Label>Arrondi des boutons et cartes : {form.boutons.arrondi ?? 12}px (défaut 12)</Label>
            <Slider value={[form.boutons.arrondi ?? 12]} min={0} max={24} step={1}
              onValueChange={([v]) => set("boutons.arrondi", v === 12 ? "" : v)} data-testid="perso-arrondi" />
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!form.boutons.gras} onCheckedChange={(v) => set("boutons.gras", v)} data-testid="perso-boutons-gras" /> Texte en gras
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!form.boutons.majuscules} onCheckedChange={(v) => set("boutons.majuscules", v)} data-testid="perso-boutons-maj" /> MAJUSCULES
            </label>
          </div>
        </div>
        <div className="rounded-lg border border-dashed border-border p-4 flex flex-wrap items-center gap-3" data-testid="perso-apercu">
          <span className="text-xs text-muted-foreground">Aperçu après enregistrement :</span>
          <Button size="sm" type="button">Bouton principal</Button>
          <Button size="sm" variant="outline" type="button">Bouton secondaire</Button>
          <RoleBadge role="gestionnaire" />
          <RoleBadge role="Exemple" badge={{ couleur: form.couleurs.sombre.accent || "#2DD4BF", majuscules: form.boutons.majuscules, gras: form.boutons.gras, police: form.typo.police_titres }} testid="perso-apercu-badge" />
        </div>
      </Section>

      <Section icon={LogIn} titre="Page de connexion" desc="Visible par tous les visiteurs, avant connexion." testid="perso-section-login">
        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Grand titre</Label>
              <Input value={form.login.titre || ""} onChange={(e) => set("login.titre", e.target.value)} placeholder="SST · SSIAP · Cartes Pro · Incendie" className="bg-background" data-testid="perso-login-titre" />
            </div>
            <div className="space-y-2">
              <Label>Texte de la carte de connexion</Label>
              <Input value={form.login.texte_carte || ""} onChange={(e) => set("login.texte_carte", e.target.value)} placeholder="Espace sécurisé — accès restreint" className="bg-background" data-testid="perso-login-carte" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Accroche (panneau gauche)</Label>
            <Input value={form.login.accroche || ""} onChange={(e) => set("login.accroche", e.target.value)} placeholder="Tout le magasin, au même endroit." className="bg-background" data-testid="perso-login-accroche" />
          </div>
          <div className="space-y-2">
            <Label>Description (panneau gauche)</Label>
            <Textarea value={form.login.description || ""} onChange={(e) => set("login.description", e.target.value)} rows={2}
              placeholder="Suivi des certifications, planification des sessions et alertes d'expiration par email…" className="bg-background" data-testid="perso-login-description" />
          </div>
          <label className="flex items-center gap-2 text-sm w-fit">
            <Switch checked={form.login.panneau_gauche !== false} onCheckedChange={(v) => set("login.panneau_gauche", v ? "" : false)} data-testid="perso-login-panneau" /> Afficher le panneau gauche (grand écran)
          </label>
          <ImageUpload kind="login" label="Image du panneau gauche" desc="Grande image verticale de la page de connexion" />
        </div>
      </Section>

      <Section icon={MenuIcon} titre="Libellés du menu" desc="Laissez vide pour conserver le libellé d'origine." testid="perso-section-menu">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {MENU_FIELDS.map((m) => (
            <div key={m.key} className="space-y-1.5">
              <Label className="text-xs">{m.def}</Label>
              <Input value={form.menu[m.key] || ""} onChange={(e) => set(`menu.${m.key}`, e.target.value)} placeholder={m.def} className="bg-background h-9 text-sm" data-testid={`perso-menu-${m.key}`} />
            </div>
          ))}
        </div>
      </Section>

      <div className="flex justify-end gap-2 pb-4">
        <Button variant="outline" onClick={() => save({}, "Personnalisation réinitialisée")} disabled={saving} data-testid="perso-reset-button-bottom">
          <RotateCcw className="h-4 w-4 mr-2" /> Tout réinitialiser
        </Button>
        <Button onClick={() => save(form, "Personnalisation enregistrée")} disabled={saving} data-testid="perso-save-button-bottom">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}
